import React, { useRef, useState } from 'react';
import { classes } from 'typestyle';
import { Taxonomies, Taxonomy } from '../../../types';
import Box from '../../layout/box/box.component';
import TaxonomyListGrouped from '../taxonomy-list-filter/taxonomy-list-filter-grouped.component';
import TaxonomyList from '../taxonomy-list-filter/taxonomy-list-filter.component';
import {
    taxonomyFilterHeader,
    taxonomyFilterHeaderLeft,
    taxonomyFilterHeaderRight,
    taxonomyFilterClearButtonClassName,
    taxonomyFilterBodyClassName,
    taxonomyFilterClassName,
    taxonomyFilterClearButtonDisabledClassName,
    taxonomyFilterWithSearchFieldClassName,
    taxonomyFilterSearchFieldWrapperClassName
} from './taxonomy-list-box.style';
import { useAppState } from '../../../hooks/use-app-state.hook';
import { getTaxonomyValidation } from '../../../utils';
import SearchField from '../../layout/form/search-field.component';
import { filterTaxonomies } from '../taxonomy-list-filter/taxonomy-list-filter.utils';

interface TaxonomyListBoxProps {
    title: string;
    taxonomyType: keyof Taxonomies;
    taxonomies: Taxonomy[];
    selectedIds?: number[];
    onChange?(newSelectedIds: number[]): void;
    grouped?: boolean;
    children?: React.ReactNode;
    className?: string;
    maxSelections?: number;
    searchField?: boolean | string;
}

const TaxonomyListBox: React.FC<TaxonomyListBoxProps> = ({
    title,
    taxonomyType,
    taxonomies,
    selectedIds,
    onChange,
    grouped = false,
    children,
    className,
    maxSelections,
    searchField,
}, ref) => {
    const [{taxonomies: allTaxonomies}] = useAppState();
    const [textFilter, changeTextFilter] = useState<string>('');
    const domHandlerRef = useRef<{
        scrollToTop: () => void
    }>(null);
    const searchFieldRef = useRef<HTMLInputElement>(null);

    const [
        fullTaxonomyList,
        missingTaxonomyIds
    ] = selectedIds ? getTaxonomyValidation(
        taxonomyType, allTaxonomies, taxonomies, selectedIds
    ) : [undefined, undefined];

    const trustTaxonomyList = fullTaxonomyList || taxonomies;
    const taxonomyListToDisplay = searchField
        ? filterTaxonomies(trustTaxonomyList, textFilter)
        : trustTaxonomyList;

    const domHandlers = {
        scrollToTop: () => domHandlerRef.current?.scrollToTop(),
        focusSearchField: () => searchFieldRef.current?.focus(),
    };

    const handleClickClearButton = (event: React.MouseEvent) => {
        event.preventDefault();

        if (textFilter.length) {
            changeTextFilter('');
        }

        domHandlers.scrollToTop();
        domHandlers.focusSearchField();
        onChange && onChange([]);
    };

    const handleChangeTextFilter = (newValue: string) => {
        changeTextFilter(newValue);
        domHandlers.scrollToTop();
    };

    const handleChangeTaxonomyList = onChange
        ? (newSelectedIds: number[]) => {
            onChange(newSelectedIds);
            domHandlers.focusSearchField();
        }
        : undefined;

    return (
        <Box className={ classes(
            taxonomyFilterClassName,
            searchField && taxonomyFilterWithSearchFieldClassName,
            className
        ) }>
            <div className={ taxonomyFilterHeader }>
                <div className={ taxonomyFilterHeaderLeft }>
                    { title }
                </div>
                <div className={ taxonomyFilterHeaderRight }>
                    <button
                        type='button'
                        className={ classes(
                            taxonomyFilterClearButtonClassName,
                            !(selectedIds?.length) ? taxonomyFilterClearButtonDisabledClassName : undefined
                        ) }
                        onClick={ handleClickClearButton }
                    >
                        CLEAR
                    </button>
                </div>
            </div>
            { searchField && (
                <div className={ taxonomyFilterSearchFieldWrapperClassName }>
                    <SearchField
                        ref={ searchFieldRef }
                        value={ textFilter }
                        placeholder={ typeof searchField === 'string' ? searchField : undefined }
                        onChange={ handleChangeTextFilter }/>
                </div>
            )}
            <div className={ taxonomyFilterBodyClassName }>
                { grouped ? (
                    <TaxonomyListGrouped
                        ref={ domHandlerRef }
                        list={ taxonomyListToDisplay }
                        selectedIds={ selectedIds }
                        onChange={ handleChangeTaxonomyList }
                        searchField={ grouped }
                        children={ children }
                        maxSelections={ maxSelections }
                        strikethroughIds={ missingTaxonomyIds }/>
                ) : (
                    <TaxonomyList
                        list={ taxonomyListToDisplay }
                        selectedIds={ selectedIds }
                        onChange={ handleChangeTaxonomyList }
                        children={ children }
                        maxSelections={ maxSelections }
                        strikethroughIds={ missingTaxonomyIds }/>
                )}
            </div>
        </Box>
    );
};

export default TaxonomyListBox;
